/**
 * A walking character's true point (ADR-116's amendment of 2026-10-07, the rule the coordinator chose under the
 * author's rulings of that day). A position is a lattice point, whole sixteenths of a tile, and that is what collides,
 * what is judged and what is saved. A speed is finer, 16 speed units to a sixteenth, so a step is seldom a whole
 * number of sixteenths. Each axis therefore carries an offset: how far the point the character has truly walked to
 * lies from its lattice point, in speed units, from -8 to 7. A step adds the ruled move to the offset, the lattice
 * point moves to the lattice point nearest the true one, and the offset keeps the rest. Nothing a page sends chooses
 * a step's length, the lattice point is never more than half a sixteenth from the true point, and the true point
 * moves exactly the ruled speed, so no choice of directions covers more ground than a straight walk and an honest
 * walk loses none. Integer maths, no grid and no allocation; the map server and the page run the same functions.
 */

/**
 * Speed units to a sixteenth (ADR-116 decision 25): a speed is counted in units a sixteenth of a sixteenth, 256 to a
 * tile, while a position stays in whole sixteenths. The rules' `walk_speed`, `run_speed` and `creature_speed` are in
 * this unit.
 */
export const SPEED_UNITS = 16;

/**
 * The shift that divides by `SPEED_UNITS`, rounding down for a negative number too.
 */
const SPEED_SHIFT = 4;

/**
 * Half a sixteenth in speed units: what rounds a true coordinate to the nearest lattice coordinate.
 */
const HALF_SIXTEENTH = SPEED_UNITS / 2;

/**
 * The least and the greatest offset an axis carries, in speed units: the true point lies from half a sixteenth before
 * its lattice point to just under half a sixteenth past it.
 */
export const OFFSET_MIN = -HALF_SIXTEENTH;
export const OFFSET_MAX = HALF_SIXTEENTH - 1;

/**
 * The offsets of a character's true point from its lattice point, one an axis, in speed units from `OFFSET_MIN` to
 * `OFFSET_MAX`. Plain data the map server keeps for each character and the page's predictor keeps for its own; a
 * snapshot carries both in one byte (`packOffsets`). A new character, and one the map server places, starts at 0 on
 * both.
 */
export interface Offsets {
  x: number;
  y: number;
}

/**
 * The speed each axis of a diagonal step moves at: the speed over root two, rounded down to whole speed units by
 * integer maths, so a diagonal walk is never faster than a cardinal one at any speed (`2 * share * share` is at most
 * `units * units`), and at the shipped walk and run it is 98.7 and 99 percent of one.
 *
 * @param units The speed in speed units, a whole number from 0 to 46340.
 * @returns The per-axis speed in speed units, `floor(units / sqrt 2)`.
 * @example
 * diagonalUnits(43);
 * // 30
 */
export function diagonalUnits(units: number): number {
  let share = (units * 46341) >> 16;
  while (2 * share * share > units * units) {
    share -= 1;
  }
  return share;
}

/**
 * How far the lattice coordinate moves when the true coordinate moves: to the lattice coordinate nearest the new true
 * one. For a move of at least one sixteenth it is at least one sixteenth, and it is the move rounded down or up.
 *
 * @param offset The axis's offset before the move, in speed units.
 * @param move The true move in speed units, signed: the speed along a cardinal's axis, the diagonal's share along
 *   each axis of a diagonal.
 * @returns The lattice move in whole sixteenths, signed as the move.
 * @example
 * latticeMove(0, 43);
 * // 3: the true coordinate is 2.6875 sixteenths on, and 3 is the nearest
 */
export function latticeMove(offset: number, move: number): number {
  return (offset + move + HALF_SIXTEENTH) >> SPEED_SHIFT;
}

/**
 * The offset an axis carries after a true move that the lattice followed in full (`latticeMove`).
 *
 * @param offset The axis's offset before the move, in speed units.
 * @param move The true move in speed units, signed.
 * @returns The offset after, from `OFFSET_MIN` to `OFFSET_MAX`.
 */
export function offsetAfter(offset: number, move: number): number {
  return ((offset + move + HALF_SIXTEENTH) & (SPEED_UNITS - 1)) - HALF_SIXTEENTH;
}

/**
 * The offset an axis carries when a wall stopped its lattice move short: the true point is held as far toward the
 * wall as its lattice point allows, so the ground the wall took is lost and none is gained by leaving it.
 *
 * @param move The true move that was stopped, in speed units, signed.
 * @returns `OFFSET_MAX` for a move toward greater coordinates, `OFFSET_MIN` toward lesser.
 */
export function offsetAgainst(move: number): number {
  return move > 0 ? OFFSET_MAX : OFFSET_MIN;
}

/**
 * The least a number of unobstructed steps in a row cover along an axis, whatever offset the walk starts with: the
 * even distance rounded down. What a rule may count on however a character stands.
 *
 * @param units The speed in speed units.
 * @param steps How many steps.
 * @returns The sixteenths every such walk covers at least.
 */
export function leastCover(units: number, steps: number): number {
  return (units * steps) >> SPEED_SHIFT;
}

/**
 * The most a number of unobstructed steps in a row cover along an axis, whatever offset the walk starts with: the
 * even distance rounded up. The bound on what a character moves in so many steps.
 *
 * @param units The speed in speed units.
 * @param steps How many steps.
 * @returns The sixteenths no such walk exceeds.
 */
export function mostCover(units: number, steps: number): number {
  return (units * steps + SPEED_UNITS - 1) >> SPEED_SHIFT;
}

/**
 * Where a coordinate's true point lies, in sixteenths: what a page draws a character at.
 *
 * @param coordinate The lattice coordinate in sixteenths.
 * @param offset The axis's offset in speed units.
 * @returns The true coordinate, fractional.
 */
export function trueCoordinate(coordinate: number, offset: number): number {
  return coordinate + offset / SPEED_UNITS;
}

/**
 * Packs two offsets into the byte a snapshot carries: x in the high four bits and y in the low four, each its offset
 * less `OFFSET_MIN`.
 *
 * @param x The x offset, `OFFSET_MIN` to `OFFSET_MAX`.
 * @param y The y offset.
 * @returns The byte, 0 to 255; 0x88 for no offset on either axis.
 */
export function packOffsets(x: number, y: number): number {
  return ((x - OFFSET_MIN) << 4) | (y - OFFSET_MIN);
}

/**
 * The x offset a snapshot's byte carries.
 *
 * @param packed The byte from `packOffsets`.
 * @returns The x offset.
 */
export function unpackOffsetX(packed: number): number {
  return (packed >> 4) + OFFSET_MIN;
}

/**
 * The y offset a snapshot's byte carries.
 *
 * @param packed The byte from `packOffsets`.
 * @returns The y offset.
 */
export function unpackOffsetY(packed: number): number {
  return (packed & (SPEED_UNITS - 1)) + OFFSET_MIN;
}
